import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { useContact } from '../context/ContactContext';
import Logo from './Logo';
import './Footer.css';

function Footer() {
  const currentYear = new Date().getFullYear();
  const { contactSettings } = useContact();

  return (
    <footer className="footer" style={{ background: `linear-gradient(135deg, ${contactSettings.footerColor || '#2d3748'} 0%, ${contactSettings.footerGradient || '#1a202c'} 100%)` }}>
      <div className="footer-container">
        {/* Top Section */}
        <div className="footer-top">
          <div className="footer-column footer-about">
            <Logo size="small" showText={true} />
            <p className="footer-description">
              Your trusted partner for the best flight deals, hotels, cruises, and vacation packages. 
              We make travel affordable and unforgettable.
            </p>
            <div className="footer-social">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Facebook size={20} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Twitter size={20} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Instagram size={20} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/flights">Flights</Link></li>
              <li><Link to="/hotels">Hotels</Link></li>
              <li><Link to="/cruises">Cruises</Link></li>
              <li><Link to="/packages">Vacation Packages</Link></li>
              <li><Link to="/deals">Special Deals</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Company</h3>
            <ul className="footer-links">
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/why-choose-us">Why Choose Us</Link></li>
              <li><Link to="/careers">Careers</Link></li>
              <li><Link to="/blog">Travel Blog</Link></li>
              <li><Link to="/reviews">Customer Reviews</Link></li>
              <li><Link to="/partnerships">Partnerships</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Support</h3>
            <ul className="footer-links">
              <li><Link to="/help">Help Center</Link></li>
              <li><Link to="/faq">FAQs</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/booking-guide">Booking Guide</Link></li>
              <li><Link to="/cancellation">Cancellation Policy</Link></li>
              <li><Link to="/terms">Terms & Conditions</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
            </ul>
          </div>

          <div className="footer-column footer-contact">
            <h3 className="footer-heading">Contact Us</h3>
            <ul className="footer-contact-list">
              <li>
                <Phone size={18} />
                <div>
                  <a href={`tel:${contactSettings.tfn.replace(/[^0-9+]/g, '')}`} className="contact-link">
                    {contactSettings.tfn}
                  </a>
                  <span className="contact-label">{contactSettings.workingHours || '24/7 Customer Support'}</span>
                </div>
              </li>
              <li>
                <Mail size={18} />
                <div>
                  <a href={`mailto:${contactSettings.email}`} className="contact-link">
                    {contactSettings.email}
                  </a>
                  <span className="contact-label">Email Support</span>
                </div>
              </li>
              <li>
                <MapPin size={18} />
                <div>
                  <span className="contact-text">{contactSettings.address.split(',')[0]}</span>
                  <span className="contact-label">{contactSettings.address.split(',').slice(1).join(',').trim()}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="footer-newsletter">
          <div className="newsletter-content">
            <h3>Subscribe to Our Newsletter</h3>
            <p>Get exclusive deals and travel tips delivered to your inbox</p>
          </div>
          <div className="newsletter-form">
            <input 
              type="email" 
              placeholder="Enter your email address" 
              className="newsletter-input"
            />
            <button className="newsletter-button">Subscribe</button>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">
              {(contactSettings.copyrightText || `© ${currentYear} FlyTravelDeals.us. All rights reserved.`).replace('{year}', currentYear)}
            </p>
            <div className="footer-badges">
              <span className="badge">🔒 Secure Booking</span>
              <span className="badge">✓ Best Price Guarantee</span>
              <span className="badge">⭐ 24/7 Support</span>
            </div>
            <div className="footer-legal">
              <Link to="/privacy">Privacy Policy</Link>
              <span className="divider">|</span>
              <Link to="/terms">Terms of Service</Link>
              <span className="divider">|</span>
              <Link to="/cookies">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
